<template>
  <VDialogV3
    id="payment-requisites-dialog"
    position="right"
    width="600px"
    :entity="localPaymentRequest"
    @submit="onSubmit"
    @close="onClose"
  >
    <section>
      <header>Основное</header>
      <VSelectV2
        :model-value="localPaymentRequest.receiver"
        label="Продавец"
        :data="receiverSource"
        required
        :disabled="localPaymentRequest.isExisting === true"
        :disabled-reasons="localPaymentRequest.isExisting === true ? 'Запрещено: платёж уже создан' : null"
        @update:model-value="onReceiverChange"
      />

      <VSelectV2
        v-model="localPaymentRequest.receiverCompany"
        label="Юр лицо"
        :disabled="isNewDocument === false"
        hide-clear-btn
        :data="receiverCompanySource"
        required
      />

      <VSelectV2
        v-model="localPaymentRequest.receiverBankAccount"
        label="Расчётный счёт"
        :data="receiverBankAccountSource"
        :required="localPaymentRequest.isForeign === false"
      >
        <template #option="{ option }">
          {{ option.toString() }}
          <div
            v-if="option.isMain === true"
            class="description"
          >
            Основной
          </div>
        </template>
      </VSelectV2>
    </section>

    <section>
      <VSelectV2
        v-model="localPaymentRequest.reasonType"
        label="Основание платежа"
        :data="reasonTypeSource"
        hide-clear-btn
        required
        :disabled="isNewDocument === false"
      />

      <VSelectV2
        v-if="isOrderPositionReasonType === true"
        key="positionInput"
        :model-value="localPaymentRequest.orderPosition"
        @update:modelValue="onOrderPositionSelected"
        :data="orderPositionsSource"
        label="Позиция"
        hide-clear-btn
        required
      />

      <p
        v-if="isOrderPositionReasonType === true && orderPositionsSource.isLoadedAndEmpty === true"
        class="empty-documents"
      >
        <VIcon
          theme="outlined"
          material="info"
        />
        Нет неоплаченных позиций
      </p>

      <VSelectV2
        v-if="isShipmentReasonType === true"
        key="shipmentInput"
        :model-value="localPaymentRequest.shipment"
        @update:modelValue="onShipmentSelected"
        label="Отгрузка"
        :data="shipmentsSource"
        hide-clear-btn
        required
      />

      <p
        v-if="isShipmentReasonType === true && shipmentsSource.isLoadedAndEmpty === true"
        class="empty-documents"
      >
        <VIcon
          theme="outlined"
          material="info"
        />
        Нет неоплаченных отгрузок
      </p>

      <VSelectV2
        v-if="isOrderReasonType === true"
        key="orderInput"
        :model-value="localPaymentRequest.order"
        @update:modelValue="onOrderSelected"
        label="Заказ"
        :data="ordersSource"
        hide-clear-btn
        required
      />

      <p
        v-if="isOrderReasonType === true && ordersSource.isLoadedAndEmpty === true"
        class="empty-documents"
      >
        <VIcon
          theme="outlined"
          material="info"
        />
        Нет неоплаченных заказов
      </p>

      <!-- Ограничения списков документов. Список сужен, и об этом надо
           сказать: иначе отсутствие нужного документа выглядит как сбой.
           Каждое снимается и возвращается одной ссылкой — оплатить чужой
           документ иногда нужно, а крестик снял бы фильтр безвозвратно, до
           переоткрытия формы.

           Стоят один раз после обоих селектов, а не под каждым: в один
           момент виден только один список, и дублировать строку незачем. -->
      <dl
        v-if="isDocumentSelectVisible === true && hasDocumentFilters === true"
        class="document-filters"
      >
        <template v-if="isBasisOrderPositionFilterVisible === true">
          <dt>Документы с позицией</dt>
          <dd :class="{ inactive: isBasisOrderPositionFilterApplied === false }">
            №{{ $core.utils.string.formatWithDelimiter(basisOrderPosition.id) }}
          </dd>
          <dd>
            <VLink @click="toggleBasisOrderPositionFilter">
              {{ filterActionText(isBasisOrderPositionFilterApplied) }}
            </VLink>
          </dd>
        </template>

        <template v-if="$core.utils.isNullOrUndefined(localPaymentRequest.responsible) === false">
          <dt>Документы, где ответственный</dt>
          <dd
            class="ellipsis"
            :class="{ inactive: isResponsibleFilterApplied === false }"
            :title="localPaymentRequest.responsible.name ?? undefined"
          >
            {{ localPaymentRequest.responsible.name }}
          </dd>
          <dd>
            <VLink @click="toggleResponsibleFilter">
              {{ filterActionText(isResponsibleFilterApplied) }}
            </VLink>
          </dd>
        </template>
      </dl>

      <!-- Мультивалютный пикер при заказе в нескольких валютах -->
      <div
        v-if="showOrderCurrencySelect === true && localPaymentRequest.order !== null"
        class="order-currency"
      >
        <div
          v-for="[currencyCode, currencyPositions] in localPaymentRequest.order.positionsByCurrency"
          :key="currencyCode"
          class="order-currency-item"
          :class="{
						selected: localPaymentRequest.orderCurrencyCode === currencyCode,
					}"
          @click="onOrderPositionCurrencyInput(currencyCode)"
        >
          <header>Оплатить {{ currencyCode }}</header>

          <div
            v-for="position in currencyPositions"
            :key="position.id"
            class="order-position"
          >
            <div
              class="name ellipsis"
              :title="position.name ?? undefined"
            >
              {{ position.name }}
            </div>
            <div class="sum">
              {{ position.sumFullSeller.format(currencyCode) }}
            </div>
          </div>
        </div>
      </div>

      <VSelectV2
        v-model="localPaymentRequest.orderCurrency"
        label="Валюта сделки"
        :data="currencySource"
        hide-clear-btn
        :disabled="orderCurrencyDisabled"
      >
        <template #option="{ option }">
          <div>{{ option.description }}</div>
          <div class="description">
            {{ option.code }}
          </div>
        </template>
      </VSelectV2>

      <template v-if="invoiceControlsVisible === true && localPaymentRequest.invoice !== null">
        <VInput
          key="invoiceNumberInput"
          v-model="localPaymentRequest.invoice.number"
          label="Номер счёта"
          layout="vertical"
          required
          @update:modelValue="updateReasonText"
        />

        <VDateInput
          key="invoiceDateInput"
          v-model="localPaymentRequest.invoice.date"
          label="Дата счёта"
          layout="vertical"
          required
          @update:modelValue="updateReasonText"
        />

        <VSelectV2
          key="invoiceCurrencyInput"
          v-model="localPaymentRequest.invoice.currency"
          label="Валюта счёта"
          :data="currencySource"
          hide-clear-btn
          :disabled="invoiceCurrencyDisabled === true || isNewDocument === false"
          required
          @update:modelValue="onInvoiceCurrencyInput"
        >
          <template #option="{ option }">
            <div>{{ option.description }}</div>
            <div class="description">
              {{ option.code }}
            </div>
          </template>
        </VSelectV2>

        <!-- Сумма счёта спрашивается только при произвольном курсе: там она
             его и задаёт, отношением к сумме основания. При прямом платеже и
             по курсу ЦБ конвертировать нечего, и поле не нужно. -->
        <VInputNumberRusV2
          v-if="invoiceSumVisible === true"
          key="invoiceSumInput"
          :type="Sum"
          label="Сумма счёта"
          layout="vertical"
          required
          :model-value="localPaymentRequest.invoice.sum"
          :unit="localPaymentRequest.invoice.currency?.symbol ?? ''"
          @update:modelValue="onInvoiceSumInput"
        >
          <template #helper-text>
            Из неё считается курс платежа
          </template>
        </VInputNumberRusV2>

      </template>

      <!-- Тип оплаты и курс — только когда валюта сделки не локальная,
           иначе конвертировать нечего.

           Стоят здесь, а не в блоке платежа: тип выводится из валюты сделки
           и валюты счёта, то есть из полей выше. Стоя раньше них, он
           показывал промежуточное значение и переставлялся сам уже после
           того, как форма наполовину заполнена. -->
      <template v-if="showCourseControls === true">
        <VCheckboxGroup
          v-model="localPaymentRequest.paymentType"
          label="Тип оплаты"
          layout="vertical"
          type-radio
          required
          :options="paymentTypeOptions"
          option-label="name"
          :disabled="isNewDocument === false"
        />

        <!-- Курс только показывается, и только когда он уже посчитан. Задаёт
             его сумма, введённая в позицию платежа: в счёте написана сумма, а
             не курс, и считать курс в уме пользователю незачем.

             Пустое поле здесь показывать нечего и нечем заполнить — позиции
             лежат в карточке, за этим диалогом. Поэтому пока курса нет, поля
             нет, а что делать, говорит сама карточка причиной
             reasonUnavailableSubmit, прямо под гридом. -->
        <VInputNumberRusV2
          v-if="localPaymentRequest.paymentType?.id === 'byCustomRate' && localPaymentRequest.hasCurrencyRate === true"
          key="customCurrencyRateInput"
          :type="CurrencyRate"
          label="Курс"
          layout="vertical"
          disabled
          :model-value="localPaymentRequest.paymentRequestCurrencyRate"
        />
      </template>
    </section>

    <section>
      <template v-if="userCanCreateManual === true">
        <VSwitch
          v-model="localPaymentRequest.isManual"
          theme="slider"
          layout="vertical"
          checked-label="Регистрация внешнего платежа"
          unchecked-label="Обычный платёж"
        />
      </template>

      <VInput
        v-if="localPaymentRequest.isManual === true"
        v-model="localPaymentRequest.externalNumber"
        label="Внешний номер платежа"
        layout="vertical"
        required
        :disabled="localPaymentRequest.isManual !== true && userCanCreateManual === false"
      />
    </section>

    <hr>

    <section>
      <header>Платёж</header>
      <VInput
        label="Валюта платежа"
        layout="vertical"
        disabled
        :value="localPaymentRequest.actualPaymentCurrency?.description"
      />

      <VDateInput
        v-model="localPaymentRequest.plannedPaymentDate"
        label="Дата оплаты"
        layout="vertical"
        required
        :validate-method="validatePaymentDate"
      >
        <template #helper-text>
          <div
            v-if="paymentDays.size > 0"
            class="date-list"
          >
            <span class="label">Плановые даты оплат:</span>
            <ul>
              <li
                v-for="(date, index) in paymentDays.values()"
                :key="index"
              >
                {{ $core.utils.date.format(date) }}
              </li>
            </ul>
          </div>
        </template>
      </VDateInput>

      <!-- Процент оплаты - операция ввода, а не поле документа: он
           раскладывается по количествам позиций и больше нигде не живёт.
           Показываемое значение выводится из снимка основания, поэтому
           переживает и сохранение, и редактирование заказа. -->
      <VInputNumberRus
        v-if="localPaymentRequest.paymentType?.id !== 'byDocumentExactly'"
        :value="localPaymentRequest.paymentSharePercent"
        label="Процент оплаты"
        layout="vertical"
        :max="100"
        @input="value => localPaymentRequest.applyPaymentPercent(value)"
      />

      <VInputNumberRusV2
        key="paymentRequestSumFull"
        v-model="localPaymentRequest.paymentRequestSumFull"
        :type="Sum"
        label="Итоговая сумма"
        layout="vertical"
        disabled
        required
      />

      <VInputNumberRusV2
        key="paymentRequestVatSum"
        v-model="localPaymentRequest.paymentRequestVatSum"
        :type="Sum"
        label="Сумма НДС"
        layout="vertical"
        disabled
        required
      />
    </section>

    <hr>

    <section>
      <!-- Обязательное и при редактировании. Раньше стояло :required на
           создании, но бэк требует основание всегда: продуктовая категория
           позиции может быть с ним связана, и пустое основание валидацию не
           проходит. Старый платёж без основания всё равно не сохранить,
           так что честнее сказать об этом сразу, а не на сабмите. -->
      <VSelectV2
        v-model="localPaymentRequest.reasonBasis"
        label="Что оплачивается"
        required
        :data="$core.dictionaries.paymentReasonBases"
        @update:modelValue="updateReasonText"
      />

      <!-- Дата плана производства — фишка ЛСЗ: остальным партнёрам поле не
           заполняется автоматически и не нужно. Тем же условием оно
           инициализируется в PaymentRequest.initManufactureDate. -->
      <VDateCustomFormatInput
        v-if="$core.currentPartner?.isLSZ === true"
        mode="month"
        :value="localPaymentRequest.manufactureDate"
        label="Дата плана производства"
        layout="vertical"
        required
        :disabled="localPaymentRequest.isPaid"
        @input="value => localPaymentRequest.manufactureDate = value"
      >
        <template #helper-text>
          Полная дата: {{ productionPlanDate ?? '--' }}
        </template>
      </VDateCustomFormatInput>

      <VSelectV2
        v-model="localPaymentRequest.productionSite"
        label="Производственная площадка"
        disabled
      />

      <!-- ЦФО — предустанавливается из площадки, можно переопределить. Только Наманган -->
      <VSelectV2
        v-if="$core.currentPartner?.isNamangan === true"
        v-model="localPaymentRequest.swCostscenter"
        label="ЦФО"
        :data="swCostscentersSource"
      />

      <!-- Назначение платежа Oracle — загружается по reasonBasis + productionSite -->
      <VSelectV2
        v-model="localPaymentRequest.swPaymentDestination"
        label="Назначение платежа"
        :data="swPaymentDestinationsSource"
      />

      <!-- Статья бюджета и текст назначения — только Наманган -->
      <template v-if="$core.currentPartner?.isNamangan === true">
        <!-- Статья бюджета — загружается по swPaymentDestination -->
        <VSelectV2
          v-model="localPaymentRequest.swBudgetItem"
          label="Статья бюджета"
          :data="swBudgetItemsSource"
        >
          <template #option="{ option }">
            <div>{{ option.name }}</div>
            <div class="budget-item-code">{{ option.code }}</div>
          </template>
        </VSelectV2>

        <VTextarea
          v-model="localPaymentRequest.reasonText"
          label="Назначение платежа"
          layout="vertical"
          disabled
        />
      </template>
    </section>

    <hr>

    <section>
      <VSelectV2
        v-model="localPaymentRequest.priorityType"
        label="Приоритет платежа"
        :data="$core.dictionaries.paymentPriorityTypes"
      />

      <VTextarea
        v-model="localPaymentRequest.commentary"
        label="Комментарий"
        layout="vertical"
      />
    </section>

    <hr>

    <section v-if="typeof localPaymentRequest.isOfferBased === 'boolean'">
      <VSwitch
        v-model="localPaymentRequest.isOfferBased"
        theme="slider"
        disabled
        checked-label="Создан на основе предложения"
        unchecked-label="Создан не на основе предложения"
        layout="vertical"
      />
    </section>

    <hr>

    <section>
      <VSelectV2
        v-model="localPaymentRequest.responsible"
        label="Ответственный"
        placeholder="Введите ФИО ответственного"
        hide-clear-btn
        :data="responsibleSource"
        required
      />
    </section>
  </VDialogV3>
</template>

<style lang="scss" src="./PaymentRequestRequisites.scss" />

<script lang="ts">

import DateSimple                   from '@/core/types/DateSimple';

import PaymentRequest               from '@/domains/PaymentRequests/PaymentRequest';
import PaymentRequestsService       from '@/domains/PaymentRequests/PaymentRequestsService';
import PaymentType                  from '@/domains/PaymentRequests/PaymentType';
import SWCostscenter                from '@/domains/SWCostscenters/SWCostscenter';
import SWPaymentDestinationsService from '@/domains/SWPaymentDestinations/SWPaymentDestinationsService';
import SWBudgetItemsService         from '@/domains/SWBudgetItems/SWBudgetItemsService';
import PartnersService              from '@/domains/Partners/PartnersService';
import ShipmentsService             from '@/domains/Shipments/ShipmentsService';
import OrdersService                from '@/domains/Orders/OrdersService';
import CompaniesService             from '@/domains/Companies/CompaniesService';
import ProductionSitesService       from '@/domains/ProductionSites/ProductionSitesService';
import Sum                          from "@/core/types/Sum";
import CurrencyRate                 from "@/core/types/CurrencyRate";

import type Shipment               from '@/domains/Shipments/Shipment';
import type Order                  from '@/domains/Orders/Order';
import type Partner                from '@/domains/Partners/Partner';
import type Company                from '@/domains/Companies/Company';
import type OrderPosition          from '@/domains/Orders/OrderPosition';
import type Person                 from '@/domains/Persons/Person';

export default defineComponent({
  name: 'PaymentRequestRequisites',

  props: {
    paymentRequest: {
      type: PaymentRequest,
      required: true,
    },
  },

  emits: ['save', 'close'],

  setup() {
    return {
      Sum,
      CurrencyRate,
      PaymentRequestsService,
    }
  },

  data() {
    const localPaymentRequest = this.$core.utils.cloneDeep(this.paymentRequest);
    const isNewDocument       = this.$core.utils.isNullOrUndefined(localPaymentRequest.id);

    return {
      isNewDocument,
      localPaymentRequest,

      /**
       * Позиция заказа, с которой начали оформление платежа.
       *
       * Живёт в компоненте, а не в модели: платежу она не нужна, как только
       * основанием стал другой документ, а форме нужна — ей она сужает
       * списки отгрузок и заказов до тех, что эту позицию содержат. Модель
       * свою ссылку на позицию при смене типа основания обнуляет, поэтому
       * помнить приходится здесь.
       */
      basisOrderPosition: localPaymentRequest.orderPosition ?? null,

      /**
       * Ограничение списков по этой позиции применено. Отдельный признак, а
       * не обнуление basisOrderPosition: саму позицию помнить надо и со
       * снятым фильтром — иначе вернуть ограничение будет нечем.
       */
      isBasisOrderPositionFilterApplied: true,

      /**
       * Ограничение списков по ответственному применено. Свои сделки платит
       * тот же, кто их ведёт, в подавляющем большинстве случаев, поэтому по
       * умолчанию включено; оплата за коллегу снимает его ссылкой.
       */
      isResponsibleFilterApplied: true,

      receiverSource: this.$core.loader.select(({ pattern }) => PartnersService.searchSellers(pattern)),

      receiverCompanySource: this.$core.loader.select(({ receiver }) => CompaniesService.getList({ partnerId: receiver.id }))
        .dependsOn({ receiver: () => this.localPaymentRequest.receiver })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

      receiverBankAccountSource: this.$core.loader.select(({ receiverCompany }) => receiverCompany.bankAccounts ?? [])
        .dependsOn({ receiverCompany: () => this.localPaymentRequest.receiverCompany })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

      reasonTypeSource: this.$core.loader.select(({ pattern }) => {
        const reasonTypes = PaymentRequestsService.reasonTypes;
        if (!pattern) return reasonTypes;

        return reasonTypes.filter(item => {
          return item.name.toLowerCase().includes(pattern.toLowerCase());
        });
      }).preloadOnMount(),

      shipmentsSource: this.$core.loader.select(({ receiver, receiverCompany, basisOrderPosition, responsible }) => ShipmentsService.getList({
        seller:            { id: receiver.id },
        sellerCompany:     { id: receiverCompany.id },
        woPaymentRequests: true,
        idPattern:         basisOrderPosition?.id,
        responsible:       responsible,
      }))
        .dependsOn({
          receiver:        () => this.localPaymentRequest.receiver,
          receiverCompany: () => this.localPaymentRequest.receiverCompany,
        })
        .dependsOnNullable({
          basisOrderPosition: () => this.basisOrderPositionFilter,
          responsible:        () => this.responsibleFilter,
        })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

      ordersSource: this.$core.loader.select(({ receiver, receiverCompany, basisOrderPosition, responsible }) => OrdersService.getOrdersList({
        seller:            { id: receiver.id },
        sellerCompany:     { id: receiverCompany.id },
        woPaymentRequests: true,
        idPattern:         basisOrderPosition?.id,
        responsible:       responsible,
      }))
        .dependsOn({
          receiver:        () => this.localPaymentRequest.receiver,
          receiverCompany: () => this.localPaymentRequest.receiverCompany,
        })
        .dependsOnNullable({
          basisOrderPosition: () => this.basisOrderPositionFilter,
          responsible:        () => this.responsibleFilter,
        })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

      orderPositionsSource: this.$core.loader.select(({ receiver, receiverCompany, responsible }) => OrdersService.getPositionsList({
        seller:         { id: receiver.id },
        sellerCompany:  { id: receiverCompany.id },
        paymentStatus:  'no-request',
        deliveryStatus: 'all',
        responsible:    responsible,
      }))
        .dependsOn({
          receiver:        () => this.localPaymentRequest.receiver,
          receiverCompany: () => this.localPaymentRequest.receiverCompany,
        })
        .dependsOnNullable({ responsible: () => this.responsibleFilter })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

      /**
       * Валюты, допустимые для сделки/счёта, — считает модель
       * (availableCurrencies). Источник общий для селектов «Валюта
       * сделки» и «Валюта счёта». Зависимости — ровно то, что читает
       * геттер: получатель (его локальная валюта) и код валюты сделки
       * (примитив, dependencyKey переварит без id).
       */
      currencySource: this.$core.loader.select(() => this.localPaymentRequest.availableCurrencies)
        .dependsOn({
          receiver: () => this.localPaymentRequest.receiver,
        })
        /** Валюта сделки нужна: availableCurrencies от неё зависит, и без
         *  перечитывания список остаётся тем, каким был на момент загрузки.
         *  Обязательной её сделать нельзя — пока она не выбрана, источник
         *  перестал бы грузиться вовсе. */
        .dependsOnNullable({
          orderCurrencyCode: () => this.localPaymentRequest.orderCurrencyCode,
        })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

      /**
       * ЦФО: вилки прежнего loadSwCostscenters (дозагрузка неполной
       * площадки, samples-список) живут в loadSwCostscenterOptions.
       */
      swCostscentersSource: this.$core.loader.select(({ productionSite }) => this.loadSwCostscenterOptions(productionSite))
        .dependsOn({ productionSite: () => this.localPaymentRequest.productionSite })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

      swPaymentDestinationsSource: this.$core.loader.select(
        ({ reasonBasis, productionSite }) => SWPaymentDestinationsService.getList({ paymentReasonBasis: reasonBasis, productionSite }),
      )
        .dependsOn({
          reasonBasis:    () => this.localPaymentRequest.reasonBasis,
          productionSite: () => this.localPaymentRequest.productionSite,
        })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

      swBudgetItemsSource: this.$core.loader.select(({ swPaymentDestination }) => SWBudgetItemsService.getList({ swPaymentDestination }))
        .dependsOn({ swPaymentDestination: () => this.localPaymentRequest.swPaymentDestination })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

      responsibleSource: this.$core.loader.responsible(),
    };
  },

  computed: {
    hasReasonType(): boolean {
      return this.localPaymentRequest?.reasonType?.id !== undefined
        && this.localPaymentRequest.reasonType.id !== null;
    },

    // ==================== Типы оснований ====================

    isShipmentReasonType(): boolean {
      return this.localPaymentRequest.reasonType?.requiresShipment === true;
    },

    isOrderReasonType(): boolean {
      return this.localPaymentRequest.reasonType?.requiresOrder === true;
    },

    isOrderPositionReasonType(): boolean {
      return this.localPaymentRequest.reasonType?.requiresOrderPosition === true;
    },

    /** Основание на базе заказа (order или invoiceOrder) */
    basedOrder(): boolean {
      return this.localPaymentRequest.reasonType?.id === 'order'
        || this.localPaymentRequest.reasonType?.id === 'invoiceOrder';
    },

    /** Основание на базе standalone-инвойса */
    basedInvoice(): boolean {
      return this.localPaymentRequest.reasonType?.isStandaloneInvoice === true;
    },

    /** Показывать контролы инвойса */
    invoiceControlsVisible(): boolean {
      return this.localPaymentRequest.reasonType?.requiresInvoice === true;
    },

    // ==================== Мультивалютность ====================

    showOrderCurrencySelect(): boolean {
      return this.basedOrder === true && this.localPaymentRequest.order?.isMultiCurrency === true;
    },

    /** Валюту сделки можно выбрать только у самостоятельного счёта в
     *  локальной валюте — во всех прочих случаях она берётся из основания.
     *
     *  Сравнение шло по id валюты со строкой 'RUB', а в id лежит числовой
     *  код (643 у рубля), буквенный — в code. Условие было ложно всегда, и
     *  поле стояло недоступным при любом основании. Заодно ушёл литерал:
     *  локальная валюта у Намангана не рубль. */
    orderCurrencyDisabled(): boolean {
      return !(
        this.basedInvoice
        && this.localPaymentRequest.invoice?.currency
        && this.localPaymentRequest.invoice.currency?.code === this.localPaymentRequest.actualPaymentCurrencyCode
      );
    },

    invoiceCurrencyDisabled(): boolean {
      switch (this.localPaymentRequest.reasonType?.id) {
        case 'invoiceShipment':
          if (!this.localPaymentRequest.shipment) return false;
          return this.localPaymentRequest.shipment.orderCurrencyCode === this.localPaymentRequest.actualPaymentCurrencyCode;
        case 'invoiceOrder':
          if (!this.localPaymentRequest.order) return false;
          return this.localPaymentRequest.order.currencyCode === this.localPaymentRequest.actualPaymentCurrencyCode;
        default:
          return false;
      }
    },

    // ==================== Тип оплаты ====================

    /**
     * Доступные типы оплаты. Правила живут в модели: они относятся к платежу,
     * а не к форме, и по ним же надо проверять на сохранении. Здесь была
     * вторая, слегка расходившаяся копия тех же условий.
     */
    paymentTypeOptions(): PaymentType[] {
      return this.localPaymentRequest.getAvailablePaymentTypes();
    },

    showCourseControls(): boolean {
      /** Если основание — счёт поставщика и валюта счёта локальная — прячем */
      if (
        this.localPaymentRequest.reasonType?.id === 'invoice'
        && this.localPaymentRequest?.invoice?.currency?.code === this.localPaymentRequest.actualPaymentCurrencyCode
      ) {
        return false;
      }
      return this.localPaymentRequest?.orderCurrency?.code !== this.localPaymentRequest.actualPaymentCurrencyCode;
    },

    // customCurrencyRateDisabled удалён: поле показывается только при типе
    // «произвольный курс», а он и означает, что курс вводит пользователь.
    // Условие разрешало ввод лишь при основании invoiceShipment, из-за чего
    // на остальных основаниях тип выбирался, а курс оставался прежним.
    // Ограничивать надо доступность самого типа в getAvailablePaymentTypes,
    // а не редактируемость поля под ним.

    // ==================== Ручной платёж ====================

    /** Право регистрировать внешний платёж — хардкод-список id в Person */
    userCanCreateManual(): boolean {
      return this.$core.currentUser?.canEditPaymentExternalId === true;
    },

    // ==================== Плановые даты оплат ====================

    /**
     * Плановые даты оплат — из позиций самого платежа, а не из документа
     * основания. У позиции отгрузки plannedPaymentDate пустая (она там
     * эмулирована), поэтому для платежа по отгрузке список выходил пустым
     * и подсказка под датой оплаты не показывалась вовсе. У позиции заказа
     * дата есть всегда.
     *
     * Заодно уходит ветвление по типу основания: позиции платежа собраны
     * к этому моменту при любом из них.
     */
    paymentDays(): Map<number, any> {
      const paymentDays = new Map<number, any>();

      for (const position of this.localPaymentRequest.positions) {
        const plannedPaymentDate = position.orderPosition?.plannedPaymentDate;

        if (plannedPaymentDate !== null && plannedPaymentDate !== undefined) {
          if (paymentDays.has(+plannedPaymentDate) === false) {
            paymentDays.set(+plannedPaymentDate, plannedPaymentDate);
          }
        }
      }

      return paymentDays;
    },

    productionPlanDate(): string | null {
      return this.$core.utils.date.format(this.localPaymentRequest.manufactureDate);
    },

    // ==================== Ограничение списков документов ====================

    /**
     * Позиция как ограничение списков отгрузок и заказов, либо null, когда
     * ограничение снято. Резолвер зависимости: null в dependsOnNullable
     * меняет ключ, и контрол перезагружает список уже без фильтра.
     */
    basisOrderPositionFilter(): OrderPosition | null {
      if (this.isBasisOrderPositionFilterApplied === false) {
        return null;
      }
      else {
        return this.basisOrderPosition;
      }
    },

    /**
     * Ответственный как ограничение тех же списков, либо null, когда
     * ограничение снято. Пустое поле ограничением не является: показывать
     * документы без ответственного смысла нет.
     */
    responsibleFilter(): Person | null {
      if (this.isResponsibleFilterApplied === false) {
        return null;
      }
      else {
        return this.localPaymentRequest.responsible ?? null;
      }
    },

    /** Сумма счёта нужна только там, где из неё выводится курс */
    invoiceSumVisible(): boolean {
      return this.invoiceControlsVisible === true
        && this.localPaymentRequest.paymentType?.id === 'byCustomRate';
    },

    /** Виден ли список документов — под ним и стоят строки ограничений */
    isDocumentSelectVisible(): boolean {
      return this.isShipmentReasonType === true
        || this.isOrderReasonType === true
        || this.isOrderPositionReasonType === true;
    },

    /** Ограничение по позиции — только там, где выбирают документ. Список
     *  самих позиций по ней не сужается, и подпись врала бы. */
    isBasisOrderPositionFilterVisible(): boolean {
      return this.basisOrderPosition !== null && this.isOrderPositionReasonType === false;
    },

    /** Есть ли что показать в строках ограничений под списком */
    hasDocumentFilters(): boolean {
      return this.isBasisOrderPositionFilterVisible === true
        || this.$core.utils.isNullOrUndefined(this.localPaymentRequest.responsible) === false;
    },
  },

  methods: {
    /** Другой продавец — другой платёж: собираем заново, документ-основание и позиции прежнего продавца не переносятся */
    onReceiverChange(receiver: Partner | null): void {
      if (receiver?.id === this.localPaymentRequest.receiver?.id) {
        return;
      }

      const paymentRequest = PaymentRequest.fromNew();

      /** Очистка поля — платёж с нуля без продавца */
      if (receiver !== null) {
        paymentRequest.receiver = receiver;
      }

      this.localPaymentRequest = paymentRequest;
      this.basisOrderPosition  = null;
    },

    // ==================== Закрытие / сабмит ====================

    onClose() {
      this.$emit('close');
    },

    /**
     * Реагируем на сабмит. Валидацию формы уже выполнил VDialogV3 —
     * сюда долетаем только когда форма валидна. Наше дело — отдать
     * локальный объект наверх и закрыть диалог.
     */
    onSubmit(): void {
      this.$emit('save', this.localPaymentRequest);
      this.$core.dialogs.PaymentRequestRequisites.hide();
    },

    // ==================== Загрузка данных ====================

    /**
     * Список ЦФО для площадки. Вилки прежнего loadSwCostscenters:
     * - площадка неполная (огрызок {id, name} из отгрузки, без
     *   swCostscenter) — дозагружаем полный объект и подменяем его
     *   в платеже. Возвращаем список сразу по полному объекту:
     *   dependencyKey строится по id, подмена объекта с тем же id
     *   повторную загрузку не вызовет;
     * - площадка образцов — захардкоженный список;
     * - иначе — единственный ЦФО площадки (автовыбор сделает селект).
     */
    async loadSwCostscenterOptions(site: any): Promise<SWCostscenter[]> {
      if (!site.swCostscenter && site.id !== 'sw_berdsk_research') {
        const fullSite = await ProductionSitesService.get(site.id);
        if (fullSite) {
          this.localPaymentRequest.productionSite = fullSite;
          site = fullSite;
        }
      }

      if (site.id === 'sw_berdsk_research') {
        return SWCostscenter.getSamplesPaymentList();
      } else if (site.swCostscenter) {
        return [site.swCostscenter];
      } else {
        return [];
      }
    },

    // ==================== Основание ====================

    /**
     * Выбрали отгрузку — собираем платёж заново фабрикой. Старый объект
     * не донастраиваем: в нём остаются позиции и производные поля от
     * прежнего основания.
     */
    onShipmentSelected(shipment: Shipment) {
      this.localPaymentRequest = PaymentRequest.fromShipment(shipment, {
        receiver:        this.localPaymentRequest.receiver as Partner,
        receiverCompany: this.localPaymentRequest.receiverCompany as Company,
        /** Тип основания выбран пользователем и пересборку переживает:
         *  без него фабрика ставила «Отгрузку» поверх «Счёта поставщика
         *  по отгрузке», и реквизиты счёта исчезали с формы. */
        reasonType:      this.localPaymentRequest.reasonType,
      });

      this.warnIfBasisFullyPaid();
    },

    /**
     * Документ оплачен целиком — говорим сразу, а не оставляем пользователя
     * гадать над гридом с нулями. Выбор при этом не отменяем: причина видна
     * и под гридом карточки, а отменённый выбор выглядел бы сбоем.
     */
    warnIfBasisFullyPaid(): void {
      if (this.localPaymentRequest.isBasisFullyPaid === true) {
        this.$core.error('По этому документу платить нечего — все позиции уже оплачены');
      }
    },

    /**
     * Выбрали заказ — то же, см. onShipmentSelected.
     *
     * У мультивалютного заказа валюта сделки пуста: в какой из его валют
     * платить, пользователь выбирает в разбивке по валютам над полем
     * «Валюта сделки», до выбора позиций в платеже нет.
     */
    onOrderSelected(order: Order) {
      this.localPaymentRequest = PaymentRequest.fromOrder(order, {
        receiver:        this.localPaymentRequest.receiver as Partner,
        receiverCompany: this.localPaymentRequest.receiverCompany as Company,
        reasonType:      this.localPaymentRequest.reasonType,
      });

      this.warnIfBasisFullyPaid();
    },

    /**
     * Выбрали позицию заказа — то же, см. onShipmentSelected.
     *
     * Позицию запоминаем отдельно от модели: она переживёт смену основания
     * и сузит списки отгрузок и заказов через dependsOnNullable.
     */
    onOrderPositionSelected(orderPosition: OrderPosition) {
      this.basisOrderPosition = orderPosition;

      this.localPaymentRequest = PaymentRequest.fromOrderPosition(orderPosition, {
        receiver:        this.localPaymentRequest.receiver as Partner,
        receiverCompany: this.localPaymentRequest.receiverCompany as Company,
        reasonType:      this.localPaymentRequest.reasonType,
      });

      this.warnIfBasisFullyPaid();
    },

    /**
     * Снять ограничение по позиции или вернуть его. Сам признак и читает
     * резолвер зависимости, так что списки перезагружаются сами.
     */
    toggleBasisOrderPositionFilter(): void {
      this.isBasisOrderPositionFilterApplied = this.isBasisOrderPositionFilterApplied === false;
    },

    /** То же для ограничения по ответственному */
    toggleResponsibleFilter(): void {
      this.isResponsibleFilterApplied = this.isResponsibleFilterApplied === false;
    },

    /**
     * Подпись ссылки: ограничение применено — предлагаем показать всё,
     * снято — вернуть его обратно. Одна на оба ограничения, текст про то,
     * что именно показано, стоит рядом.
     */
    filterActionText(isApplied: boolean): string {
      if (isApplied === true) {
        return 'Показать все';
      }
      else {
        return 'Вернуть ограничение';
      }
    },

    // ==================== Валюта и тип оплаты ====================

    /**
     * Выбрали валюту в мультивалютном заказе.
     *
     * Отбор позиций, тип оплаты и курс здесь больше не задаются: всё это
     * делает каскад модели. Сеттер orderCurrencyCode ставит тип оплаты той
     * же развилкой (локальная валюта — прямой, иначе по курсу ЦБ), тип
     * ставит курс и зовёт rebuildPositions, а та отбирает позиции заказа
     * в выбранной валюте.
     *
     * Прежний код делал то же самое вручную и расходился с моделью: отбор
     * позиций работал только в рублёвой ветке, а в валютной в платёж
     * попадали позиции всех валют заказа.
     */
    onOrderPositionCurrencyInput(currencyCode: string) {
      const currency = this.$core.utils.money.getCurrencyByCode(currencyCode);

      if (this.localPaymentRequest.reasonType?.id.startsWith('invoice') === true) {
        this.onInvoiceCurrencyInput(currency);
      }

      this.localPaymentRequest.orderCurrency = currency;
    },

    /**
     * Ввели сумму счёта — пересчитываем курс. Модель сама по изменению поля
     * этого не сделает: счёт лежит обычным объектом, без сеттера.
     */
    onInvoiceSumInput(value: Sum | null): void {
      if (this.localPaymentRequest.invoice !== null) {
        this.localPaymentRequest.invoice.sum = value;
        this.localPaymentRequest.applyCurrencyRateFromInvoice();
      }
    },

    onInvoiceCurrencyInput(currency: any) {
      const localInvoice        = this.localPaymentRequest.invoice;

      localInvoice.currency     = currency;
      localInvoice.currencyCode = currency?.code ?? null;

      this.localPaymentRequest.invoice = { ...localInvoice };

      /**
       * Логика выбора типа оплаты при смене валюты счёта:
       * - Валюта сделки неизвестна → по курсу ЦБ
       * - Валюты сделки и счёта совпадают → текущий тип оплаты
       * - Разошлись → произвольный курс: платим в валюте счёта, а курс
       *   задаёт пользователь — суммой из счёта либо напрямую
       */
      if (!this.localPaymentRequest.orderCurrencyCode) {
        this.localPaymentRequest.paymentType = PaymentType.BY_RATE_OF_CENTRAL_BANK;
      }
      else if (this.localPaymentRequest.orderCurrencyCode === currency?.code) {
        // Валюты совпадают — оставляем текущий тип
      }
      else {
        this.localPaymentRequest.paymentType = PaymentType.BY_CUSTOM_RATE;
      }
    },

    // ==================== Прочее ====================

    updateReasonText() {
      this.localPaymentRequest.updateReasonText();
    },

    // ==================== Валидация даты ====================

    validatePaymentDate(value: DateSimple | null): string | null {
      if (this.isNewDocument === false || this.localPaymentRequest.isManual) return null;
      if (value === null) return null;

      const paymentDate = value;

      if (paymentDate.isPast()) {
        return 'Нельзя указать прошедшую дату';
      }

      if (paymentDate.isToday()) {
        return 'Нельзя указать сегодняшнюю дату оплаты';
      }

      const availableDays = [1, 4]; // Понедельник, четверг
      if (!availableDays.includes(value.getDay())) {
        const nextAvailableDate = this.getNextAvailableDate(value, availableDays);
        return `Допустимые дни оплаты: понедельник и четверг; Следующая дата ${this.$core.utils.date.format(nextAvailableDate)}`;
      }

      return null;
    },

    getNextAvailableDate(value: Date, availableDays: number[]): Date | null {
      const dayOfWeek = value.getDay();

      let daysUntilNextAvailable = Infinity;
      let nextAvailableDate: Date | null = null;

      availableDays.forEach(day => {
        if (day < 0 || day > 6) return;

        let daysUntil = (day - dayOfWeek + 7) % 7;
        if (daysUntil === 0) daysUntil = 7;

        if (daysUntil < daysUntilNextAvailable) {
          daysUntilNextAvailable = daysUntil;
          nextAvailableDate = new Date(value);
          nextAvailableDate!.setDate(value.getDate() + daysUntil);
        }
      });

      return nextAvailableDate;
    },

  },
});
</script>
